/**
 * The Kanban status vocabulary, shared by every surface that groups by it.
 *
 * WHY THIS IS ITS OWN MODULE. This map used to live inside Tasks.tsx. The plan
 * view groups work by exactly the same columns, and a second copy would let the
 * board and the plan disagree about where a task sits — the two surfaces would
 * each be internally consistent and tell the user different things. One map,
 * imported twice.
 *
 * THE GAP THIS MODULE PRESERVED IS NOW CLOSED (row bd6ea155). Until 2026-09-03
 * `approved`, `draft` and `failed` had no entry and fell through to "To Do" —
 * 3,250 + 484 + 18 of 3,880 live tasks, so the board was very nearly one column
 * wearing four labels, and a FAILED task read as untouched work nobody had
 * started. The extraction deliberately left that alone because changing which
 * column three thousand tasks land in is a product decision.
 *
 * Bob made that decision on 2026-09-02:
 *
 *   "todo is work not approved, and approved work can be developed against"
 *   "the items that are not yet developed but in scope and defined but not
 *    approved are 'Blocked - Awaiting User Approval'"
 *   "items that are inscope but have open questions are 'Blocked Awaiting
 *    User Q&A'"
 *
 * and, on TODO specifically, that it means work DISCOVERED DURING BUILD, off the
 * critical path and tabled — NOT "not started yet". That is why the tabled lane
 * sits at the end rather than at the front: it is beside the flow, not the start
 * of it, and putting it first is what made "not approved", "tabled" and "ready to
 * build" look like one thing.
 */

export type KanbanStatus =
  | "Blocked — Awaiting User Q&A"
  | "Blocked — Awaiting User Approval"
  | "Approved"
  | "In Progress"
  | "In Review"
  | "Done"
  | "Failed"
  | "To Do (tabled)"
  | "Unrecognised";

/**
 * Reading order, left to right: the questions that must be settled, then the
 * approval, then work that may be built, then the build itself.
 *
 * "Unrecognised" is deliberately absent. It is a real lane a card can land in,
 * but it is never rendered as an empty column — an empty lane called
 * Unrecognised is noise on every board forever, while a NON-empty one is a bug
 * report. Surfaces render it only when something is in it.
 */
export const KANBAN_STATUSES: KanbanStatus[] = [
  "Blocked — Awaiting User Q&A",
  "Blocked — Awaiting User Approval",
  "Approved",
  "In Progress",
  "In Review",
  "Done",
  "Failed",
  "To Do (tabled)",
];

/**
 * Every status the registry can hold for a task or a story, mapped to the lane
 * it belongs in. Exhaustive on purpose: an unmapped status is a visible bug
 * (see `toKanban`), not a silent one, and
 * tests/test_every_registry_status_has_a_lane.py fails the build if the registry
 * gains a status this map does not name.
 */
export const statusMap: Record<string, KanbanStatus> = {
  // In scope, but we do not yet know enough to define it.
  sketch: "Blocked — Awaiting User Q&A",
  awaiting_qa: "Blocked — Awaiting User Q&A",

  // Defined, and waiting on a person to approve it.
  draft: "Blocked — Awaiting User Approval",
  in_review: "In Review",
  "in review": "In Review",

  // Approved: "approved work can be developed against". PENDING is approved work
  // already queued for an agent, so it belongs beside APPROVED and not in the
  // build lane — nothing is being built yet.
  approved: "Approved",
  pending: "Approved",
  backlog: "Approved",

  // Being built.
  in_progress: "In Progress",
  "in progress": "In Progress",
  assigned: "In Progress",
  executing: "In Progress",
  in_development: "In Progress",

  // Built, being checked.
  review: "In Review",
  human_review: "In Review",
  ci_passed: "In Review",
  built_awaiting_test: "In Review",
  in_testing: "In Review",

  // Finished.
  done: "Done",
  completed: "Done",
  closed: "Done",
  implemented: "Done",
  verified: "Done",
  automated_passed: "Done",
  human_passed: "Done",
  // Migration 749: a RESEARCH: row closed on the founder's accepted report.
  research_accepted: "Done",
  archived: "Done",

  // Tried and did not work. Never "To Do": a failed task that reads as untouched
  // is how work gets silently dropped.
  failed: "Failed",

  // Tabled side-work, off the critical path.
  todo: "To Do (tabled)",
  "to do": "To Do (tabled)",
  to_do: "To Do (tabled)",
  parked_todo: "To Do (tabled)",
  open: "To Do (tabled)",
};

/**
 * An unknown status lands in "Unrecognised", NOT in a real lane.
 *
 * The previous default sent everything it did not recognise to "To Do", which is
 * how three thousand approved tasks came to read as untouched work: the failure
 * was indistinguishable from the ordinary case. A lane nobody expects is a
 * question a person can answer; a wrong lane is one nobody will ever ask.
 */
// What a lane is CALLED on screen (founder 6441608339 D). The status string stays the lane's key,
// in data attributes, test ids and every comparison; only the words a person reads change.
const LANE_WORDS: Partial<Record<KanbanStatus, string>> = {
  "To Do (tabled)": "Set aside",
  "Blocked — Awaiting User Q&A": "Waiting on an answer",
  "Blocked — Awaiting User Approval": "Waiting on approval",
};

export function laneLabel(status: string): string {
  return LANE_WORDS[status as KanbanStatus] ?? status;
}

export function toKanban(apiStatus: string): KanbanStatus {
  return statusMap[(apiStatus ?? "").toLowerCase()] ?? "Unrecognised";
}
